import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, ApiError } from "@/api/client";
import { toast } from "sonner";
import { FlaskConical } from "lucide-react";

// Quick presets so the team can spin up a test game that crosses the
// 15-min tip-lock boundary without doing date math.
const PRESETS: { label: string; minutes: number }[] = [
  { label: "in 17 Min (knapp vor Lock)", minutes: 17 },
  { label: "in 25 Min", minutes: 25 },
  { label: "in 1 Std", minutes: 60 },
  { label: "in 5 Min (Lock sofort)", minutes: 5 },
];

function toLocalDatetimeInputValue(d: Date) {
  // <input type="datetime-local"> expects YYYY-MM-DDTHH:mm in local time
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function CreateTestGameDialog({ onCreated }: { onCreated?: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [homeTeam, setHomeTeam] = useState("Test Heim");
  const [awayTeam, setAwayTeam] = useState("Test Gast");
  const [homeFlag, setHomeFlag] = useState("🧪");
  const [awayFlag, setAwayFlag] = useState("🧪");
  const [venue, setVenue] = useState("Englputzeder-Arena");
  const [kickoff, setKickoff] = useState(() => toLocalDatetimeInputValue(new Date(Date.now() + 25 * 60_000)));

  const applyPreset = (minutes: number) => {
    setKickoff(toLocalDatetimeInputValue(new Date(Date.now() + minutes * 60_000)));
  };

  const submit = async () => {
    if (!homeTeam.trim() || !awayTeam.trim()) {
      toast.error("Beide Teams müssen einen Namen haben.");
      return;
    }
    const ko = new Date(kickoff);
    if (isNaN(ko.getTime())) {
      toast.error("Ungültiger Anpfiff.");
      return;
    }
    setBusy(true);
    try {
      await api.games.adminCreate({
        homeTeam: homeTeam.trim(),
        awayTeam: awayTeam.trim(),
        homeFlag: homeFlag.trim() || "🧪",
        awayFlag: awayFlag.trim() || "🧪",
        kickoffTime: ko.toISOString(),
        groupLabel: "TEST",
        phase: "group",
        venue: venue.trim() || null,
      });
      toast.success("Test-Spiel angelegt 🧪");
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Anlegen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="font-bold gap-1.5">
          <FlaskConical className="h-4 w-4" />
          Test-Spiel erstellen
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-accent" /> Test-Spiel anlegen
          </DialogTitle>
          <DialogDescription>
            Legt ein manuelles Spiel an (Phase <code>group</code>, Gruppe <code>TEST</code>, gameNo ab 9000).
            Wird vom API-Football-Cron <b>nicht</b> angefasst und kann jederzeit gelöscht werden.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
            <Input value={homeFlag} onChange={(e) => setHomeFlag(e.target.value)} className="w-14 text-center" maxLength={4} />
            <Input value={homeTeam} onChange={(e) => setHomeTeam(e.target.value)} placeholder="Heim-Team" />
            <Input value={awayFlag} onChange={(e) => setAwayFlag(e.target.value)} className="w-14 text-center" maxLength={4} />
            <Input value={awayTeam} onChange={(e) => setAwayTeam(e.target.value)} placeholder="Gast-Team" />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Anpfiff</label>
            <Input type="datetime-local" value={kickoff} onChange={(e) => setKickoff(e.target.value)} className="mt-1" />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => applyPreset(p.minutes)}
                  className="text-[11px] font-bold px-2.5 py-1 rounded-pill bg-muted hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  {p.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Tipps werden 15 Min vor Anpfiff gesperrt — wähle „in 17 Min" um den Lock live zu sehen.
            </p>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Ort (optional)</label>
            <Input value={venue} onChange={(e) => setVenue(e.target.value)} className="mt-1" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
            {busy ? "Lege an …" : "Test-Spiel anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
